import axios from 'axios';
import { getApiConfig } from '../../environment';
import {
  BannerSummary,
  BannerDetail,
  BannerSlot,
  CreateBannerRequest,
  UpdateBannerRequest,
  AssignBannerToSlotRequest,
  CreateSlotRequest,
  UpdateSlotRequest,
  PagedResult,
  StorefrontBanner,
} from '../../../../pages/ContentManagement/Banners/types/BannerAdmin';

const apiConfig = getApiConfig();
const baseUrl = apiConfig.apiBaseUrl;
const instance = axios.create({
  baseURL: baseUrl,
  paramsSerializer: {
    serialize: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((v) => searchParams.append(key, String(v)));
        } else if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      return searchParams.toString();
    },
  },
});

async function getAuthHeaders() {
  const token = await window.Clerk?.session?.getToken?.({ template: 'Orderly_default' });
  const storedStore = localStorage.getItem('selectedStore');
  const store = storedStore ? JSON.parse(storedStore) : null;
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'X-Context-Store-Number': store?.storeNumber || '',
    'X-Context-Franchise-Id': store?.franchiseId || '',
  };
}

type HttpMethod = 'get' | 'post' | 'put' | 'delete';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function axiosWithAuth<T>(method: HttpMethod, url: string, dataOrConfig?: any, config?: any): Promise<T> {
  const headers = await getAuthHeaders();
  const axiosConfig = { ...config, headers };

  let res;
  switch (method) {
    case 'get':
      res = await instance.get<T>(url, { ...axiosConfig, ...dataOrConfig });
      break;
    case 'post':
      res = await instance.post<T>(url, dataOrConfig, axiosConfig);
      break;
    case 'put':
      res = await instance.put<T>(url, dataOrConfig, axiosConfig);
      break;
    case 'delete':
      res = await instance.delete<T>(url, axiosConfig);
      break;
    default:
      throw new Error(`Unsupported method: ${method}`);
  }

  return res.data;
}

// ========================================
// BANNERS
// ========================================
export async function getStorefrontBanners(): Promise<StorefrontBanner[]> {
  return axiosWithAuth<StorefrontBanner[]>('get', '/content/banner');
}

export async function getAllBanners(params?: { search?: string; page?: number; pageSize?: number }): Promise<PagedResult<BannerSummary>> {
  return axiosWithAuth<PagedResult<BannerSummary>>('get', '/content/banner/admin', { params });
}

export async function getBannerById(id: number): Promise<BannerDetail> {
  return axiosWithAuth<BannerDetail>('get', `/content/banner/${id}`);
}

export async function createBanner(request: CreateBannerRequest): Promise<{ id: number }> {
  return axiosWithAuth<{ id: number }>('post', '/content/banner', request);
}

export async function updateBanner(id: number, request: UpdateBannerRequest): Promise<void> {
  await axiosWithAuth<void>('put', `/content/banner/${id}`, request);
}

export async function deleteBanner(id: number): Promise<void> {
  await axiosWithAuth<void>('delete', `/content/banner/${id}`);
}

// ========================================
// BANNER ASSIGNMENTS
// ========================================
export async function assignBannerToSlot(request: AssignBannerToSlotRequest): Promise<void> {
  await axiosWithAuth<void>('post', '/content/banner/assign', request);
}

export async function removeAssignment(assignmentId: number): Promise<void> {
  await axiosWithAuth<void>('delete', `/content/banner/assign/${assignmentId}`);
}

// ========================================
// IMAGE UPLOAD
// ========================================
export async function uploadBannerImage(file: File): Promise<string> {
  const headers = await getAuthHeaders();
  const formData = new FormData();
  formData.append('file', file);
  const response = await instance.post<{ url: string }>(
    '/content/banner/image/upload',
    formData,
    { headers: { ...headers, 'Content-Type': 'multipart/form-data' } },
  );
  return response.data.url;
}

// ========================================
// SLOTS
// ========================================
export async function getAllSlots(): Promise<BannerSlot[]> {
  return axiosWithAuth<BannerSlot[]>('get', '/content/banner/slots');
}

export async function createSlot(request: CreateSlotRequest): Promise<{ id: number }> {
  return axiosWithAuth<{ id: number }>('post', '/content/banner/slots', request);
}

export async function updateSlot(id: number, request: UpdateSlotRequest): Promise<void> {
  await axiosWithAuth<void>('put', `/content/banner/slots/${id}`, request);
}

export async function deleteSlot(id: number): Promise<void> {
  await axiosWithAuth<void>('delete', `/content/banner/slots/${id}`);
}
